import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Button,
  Tabs,
  Tab,
  Divider,
  LinearProgress,
} from '@mui/material';
import {
  PlayCircle,
  Map as MapIcon,
  Timeline,
  LocationOn,
  Info,
  TrendingUp,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { ChantierService } from '../services/chantierService';
import { PropertyService } from '../services/propertyService';
import { Chantier, Property, UserRole, PropertyStatus } from '../types';
import { useAuthStore } from '../store/authStore';
import { Architecture, Add } from '@mui/icons-material';

const ChantierViewPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [activeTab, setActiveTab] = useState(0);
  const [chantier, setChantier] = useState<Chantier | null>(null);
  const [units, setUnits] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;
      try {
        const chantierData = await ChantierService.getChantierById(id);
        if (chantierData) {
          setChantier(chantierData);
          // Fetch units associated with this chantier
          const unitsData = await PropertyService.getPropertiesByChantier(id);
          setUnits(unitsData);
        }
      } catch (error) {
        console.error('Error fetching chantier:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  if (loading) return <LinearProgress />;
  if (!chantier) return <Typography>Chantier not found.</Typography>;

  return (
    <Box sx={{ mt: -3, mx: -3 }}>
      {/* Cinematic Hero Section */}
      <Box 
        sx={{ 
          height: '70vh', 
          width: '100%', 
          position: 'relative',
          overflow: 'hidden',
          bgcolor: 'black'
        }}
      >
        <Box
          component="img"
          src={chantier.heroVideoUrl || "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?auto=format&fit=crop&q=80&w=2070"}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: 0.7
          }}
        />
        <Box 
          sx={{ 
            position: 'absolute', 
            top: 0, left: 0, right: 0, bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            color: 'white',
            textAlign: 'center',
            p: 3,
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.7) 100%)'
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography variant="h2" sx={{ fontWeight: 800, mb: 2, fontFamily: '"Playfair Display", serif' }}>
              {chantier.name}
            </Typography>
            <Typography variant="h5" sx={{ mb: 4, opacity: 0.9 }}>
              {chantier.description}
            </Typography>
            <Button 
              variant="contained" 
              size="large" 
              startIcon={<PlayCircle />}
              sx={{ bgcolor: 'white', color: 'black', '&:hover': { bgcolor: 'grey.100' } }}
            >
              Watch Drone Footage
            </Button>
          </motion.div>
        </Box>
      </Box>

      <Container maxWidth="lg" sx={{ mt: -10, position: 'relative', zIndex: 10, pb: 8 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={8}>
            <Card sx={{ borderRadius: 2, mb: 4 }}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ px: 2 }}>
                  <Tab label="The Project" icon={<Info />} iconPosition="start" />
                  <Tab label="Master Plan" icon={<MapIcon />} iconPosition="start" />
                  <Tab label="Construction" icon={<Timeline />} iconPosition="start" />
                  {user?.role === UserRole.PROMOTEUR && user.id === chantier.developerId && (
                    <Tab label="Manage Inventory" icon={<Architecture />} iconPosition="start" />
                  )}
                </Tabs>
              </Box>
              <CardContent sx={{ p: 4 }}>
                {activeTab === 0 && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                    <Typography variant="h4" sx={{ mb: 3, fontWeight: 700, fontFamily: '"Playfair Display", serif' }}>
                      About {chantier.name}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" paragraph sx={{ lineHeight: 1.8 }}>
                      {chantier.description}
                    </Typography>
                    <Grid container spacing={2} sx={{ mt: 3 }}>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="h6" color="primary">2026</Typography>
                        <Typography variant="caption" color="text.secondary">Delivery Year</Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="h6" color="primary">{units.length}</Typography>
                        <Typography variant="caption" color="text.secondary">Total Units</Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="h6" color="primary">F2 - PH</Typography>
                        <Typography variant="caption" color="text.secondary">Unit Types</Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="h6" color="primary">{chantier.location?.city}</Typography>
                        <Typography variant="caption" color="text.secondary">Location</Typography>
                      </Grid>
                    </Grid>
                  </motion.div>
                )}
                {activeTab === 1 && (
                  <Box sx={{ height: 400, bgcolor: 'grey.100', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', borderRadius: 2, position: 'relative', overflow: 'hidden' }}>
                    <Typography variant="subtitle2" sx={{ position: 'absolute', top: 16, left: 16, bgcolor: 'white', px: 2, py: 1, borderRadius: 1, boxShadow: 1, zIndex: 2 }}>
                      Select a Block
                    </Typography>
                    
                    {/* Interactive Master Plan SVG */}
                    <Box sx={{ position: 'relative', width: '80%', height: '80%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <svg viewBox="0 0 400 300" style={{ width: '100%', height: '100%', filter: 'drop-shadow(0 10px 20px rgba(0,0,0,0.1))' }}>
                        <motion.path 
                          d="M 50 150 L 150 100 L 200 130 L 100 180 Z" 
                          fill="rgba(197, 160, 89, 0.4)" 
                          stroke="#C5A059" 
                          strokeWidth="2"
                          whileHover={{ fill: "rgba(197, 160, 89, 0.8)", cursor: "pointer", y: -5 }}
                        />
                        <motion.path 
                          d="M 160 90 L 260 40 L 310 70 L 210 120 Z" 
                          fill="rgba(138, 154, 91, 0.4)" 
                          stroke="#8A9A5B" 
                          strokeWidth="2"
                          whileHover={{ fill: "rgba(138, 154, 91, 0.8)", cursor: "pointer", y: -5 }}
                        />
                        <motion.path 
                          d="M 210 140 L 310 90 L 360 120 L 260 170 Z" 
                          fill="rgba(60, 62, 63, 0.2)" 
                          stroke="#3C3E3F" 
                          strokeWidth="2"
                          whileHover={{ fill: "rgba(60, 62, 63, 0.5)", cursor: "pointer", y: -5 }}
                        />
                      </svg>
                    </Box>
                  </Box>
                )}
                {activeTab === 2 && (
                  <Box sx={{ py: 2 }}>
                    <Typography variant="h6" sx={{ mb: 3, fontWeight: 700 }}>Construction Timeline</Typography>
                    <Box sx={{ mb: 4 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>{chantier.progress.phase.toUpperCase()}</Typography>
                        <Typography variant="caption" color="text.secondary">Current Stage • {chantier.progress.percentage}%</Typography>
                      </Box>
                      <LinearProgress variant="determinate" value={chantier.progress.percentage} sx={{ height: 6, borderRadius: 3 }} />
                    </Box>
                  </Box>
                )}
                {activeTab === 3 && (
                  <Box sx={{ py: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>Project Inventory</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {units.length} total units associated with this project.
                        </Typography>
                      </Box>
                      <Button 
                        variant="contained" 
                        startIcon={<Add />} 
                        size="small"
                        onClick={() => navigate(`/properties/create?chantierId=${chantier.id}`)}
                      >
                        Add New Unit
                      </Button>
                    </Box>
                    
                    <Divider sx={{ mb: 3 }} />

                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <Card variant="outlined" sx={{ textAlign: 'center', p: 2 }}>
                          <Typography variant="h4" color="success.main" sx={{ fontWeight: 700 }}>
                            {units.filter(u => u.salesStatus === PropertyStatus.AVAILABLE).length}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">Available</Typography>
                        </Card>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Card variant="outlined" sx={{ textAlign: 'center', p: 2 }}>
                          <Typography variant="h4" color="warning.main" sx={{ fontWeight: 700 }}>
                            {units.filter(u => u.salesStatus === PropertyStatus.RESERVED).length}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">Reserved</Typography>
                        </Card>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Card variant="outlined" sx={{ textAlign: 'center', p: 2 }}>
                          <Typography variant="h4" color="error.main" sx={{ fontWeight: 700 }}>
                            {units.filter(u => u.salesStatus === PropertyStatus.SOLD).length}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">Sold</Typography>
                        </Card>
                      </Grid>
                    </Grid>

                    <Box sx={{ mt: 4, textAlign: 'center', p: 4, bgcolor: 'rgba(0,0,0,0.02)', borderRadius: 2, border: '1px dashed rgba(0,0,0,0.1)' }}>
                       <Typography variant="body2" color="text.secondary">
                         Advanced inventory management features (bulk upload, floor plans) are coming soon to the Developer Brand Portal.
                       </Typography>
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>

            <Typography variant="h5" sx={{ mb: 3, fontWeight: 700, fontFamily: '"Playfair Display", serif' }}>
              Proximity Intelligence
            </Typography>
            <Card sx={{ p: 0, overflow: 'hidden', mb: 4 }}>
              <Box sx={{ height: 300, bgcolor: 'grey.200', position: 'relative' }}>
                <Box sx={{ position: 'absolute', top: 20, right: 20, bgcolor: 'white', p: 2, borderRadius: 2, boxShadow: 2, zIndex: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Local Insights</Typography>
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" display="block">🏖️ {chantier.metadata?.timeToBeach || '10 mins'} to Beach</Typography>
                    <Typography variant="caption" display="block">🚋 {chantier.metadata?.timeToTramway || '5 mins'} to Tram</Typography>
                  </Box>
                </Box>
                <Box 
                  component="img" 
                  src="https://images.unsplash.com/photo-1524661135-423995f22d0b?auto=format&fit=crop&q=80&w=2074" 
                  sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </Box>
            </Card>

            <Typography variant="h5" sx={{ mt: 6, mb: 3, fontWeight: 700, fontFamily: '"Playfair Display", serif' }}>
              Unit Inventory
            </Typography>
            <Box sx={{ mb: 4 }}>
              <Tabs value={0} sx={{ mb: 2 }}>
                <Tab label="Available Units" />
                <Tab label="Sold Out" />
              </Tabs>
              {units.length > 0 ? (
                <Grid container spacing={2}>
                  {units.map((unit) => (
                    <Grid item xs={12} sm={6} key={unit.id}>
                      <Card sx={{ '&:hover': { boxShadow: 4 }, cursor: 'pointer' }} onClick={() => navigate(`/properties/${unit.id}`)}>
                        <Box sx={{ position: 'relative', pt: '56.25%', bgcolor: 'grey.200' }}>
                          <Box 
                            component="img"
                            src={unit.metadata.images?.[0] || "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?auto=format&fit=crop&q=80&w=2070"}
                            sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        </Box>
                        <CardContent>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{unit.metadata.title}</Typography>
                          <Typography variant="h6" color="primary" sx={{ fontWeight: 800 }}>
                            {unit.metadata.price} {unit.metadata.currency}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                            <Chip label={`${unit.metadata.bedrooms} BR`} size="small" />
                            <Chip label={`${unit.metadata.squareFootage} m²`} size="small" />
                            <Chip label={unit.salesStatus.toUpperCase()} size="small" color={unit.salesStatus === PropertyStatus.AVAILABLE ? "success" : "warning"} variant="outlined" />
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Typography color="text.secondary" variant="body2">
                  No units available for this project yet.
                </Typography>
              )}
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ position: 'sticky', top: 84 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Inquire About This Project</Typography>
                <Divider sx={{ mb: 3 }} />
                <Typography variant="body2" color="text.secondary" paragraph>
                  Our AI Advisor can help you find the best unit matching your financial strategy.
                </Typography>
                <Button variant="contained" fullWidth size="large" sx={{ py: 1.5, mb: 2 }}>
                  Chat with AI Advisor
                </Button>
                
                <Box sx={{ mt: 4, p: 2, bgcolor: 'rgba(197, 160, 89, 0.05)', borderRadius: 2, border: '1px solid rgba(197, 160, 89, 0.1)' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <LocationOn sx={{ color: 'primary.main', fontSize: 20 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Legal Status</Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {chantier.metadata?.isUrbanTitled ? 'Verified Urban Titled (Titre Foncier)' : 'Agricultural (Melkia) - Pending Titling'}
                  </Typography>
                  <Chip label="VEFA Guaranteed" size="small" color="success" sx={{ mt: 1, height: 20, fontSize: '0.65rem' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default ChantierViewPage;